/**
 * TablePager: the pager under a server-paged table.
 *
 * Replaces the Material pagination row the orders table carried: nine items
 * spread across the full width of the card with `space-around`, a pink
 * "secondary" dot for the current page and nothing saying how many rows
 * there are. This one is compact and right-aligned, says where you are in
 * words ("1 to 50 of 581 orders"), keeps the window of page numbers short
 * (first, last, the current page and its neighbours), paints the current
 * page in the action navy and the others in the light navy tint, so nothing
 * is hollow, and adds a "Go to page" field once there are more pages than
 * the window shows.
 *
 * Reusable and presentational. Give it either `hrefFor(page)` (each page is
 * a link, so the URL carries the page and Back works) or `onChange(page)`.
 *
 *   <TablePager page={3} pageCount={12} totalItems={581} pageSize={50}
 *               itemNoun="orders" hrefFor={(p) => `?page=${p}`} />
 */
import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import "./table.css";

export const ELLIPSIS = "...";

/**
 * The page numbers to show: 1, the last page, the current page and one
 * neighbour each side, with an ellipsis where pages are skipped. Never more
 * than seven entries, so the row keeps one width however many pages exist.
 */
export const pageWindow = (page, pageCount, span = 1) => {
  const total = Math.max(1, Number(pageCount) || 1);
  const current = Math.min(Math.max(1, Number(page) || 1), total);
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let p = current - span; p <= current + span; p += 1) {
    if (p >= 1 && p <= total) pages.add(p);
  }
  // Near an edge, widen the other side so the row always holds five numbers.
  if (current <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((p) => pages.add(p));
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push(ELLIPSIS);
    out.push(p);
  });
  return out;
};

const TablePager = ({
  page,
  pageCount,
  totalItems,
  pageSize,
  itemNoun = "rows",
  hrefFor,
  onChange,
  className,
}) => {
  const total = Math.max(1, Number(pageCount) || 1);
  const current = Math.min(Math.max(1, Number(page) || 1), total);
  const history = useHistory();
  const [jump, setJump] = useState("");
  useEffect(() => { setJump(""); }, [current]);

  const first = totalItems != null && pageSize ? (current - 1) * pageSize + 1 : null;
  const last = totalItems != null && pageSize ? Math.min(current * pageSize, Number(totalItems)) : null;
  const summary = totalItems != null
    ? (Number(totalItems) === 0
      ? `No ${itemNoun}`
      : first != null
        ? `${first.toLocaleString()} to ${last.toLocaleString()} of ${Number(totalItems).toLocaleString()} ${itemNoun}`
        : `${Number(totalItems).toLocaleString()} ${itemNoun}`)
    : `Page ${current} of ${total}`;

  const go = (p) => { if (onChange && p !== current && p >= 1 && p <= total) onChange(p); };

  // One element for a page, a link when the pager is URL driven.
  const Item = ({ p, children, ...rest }) => {
    if (hrefFor && !rest.disabled) {
      return <Link to={hrefFor(p)} {...rest}>{children}</Link>;
    }
    return <button type="button" onClick={() => go(p)} {...rest}>{children}</button>;
  };

  const submitJump = (e) => {
    e.preventDefault();
    const p = Number(jump);
    if (!Number.isInteger(p) || p < 1 || p > total) return;
    if (hrefFor) history.push(hrefFor(p));
    else go(p);
  };

  if (total <= 1 && totalItems == null) return null;

  return (
    <nav className={`lk-pager${className ? ` ${className}` : ""}`} aria-label="Pagination">
      <span className="lk-pager-summary">{summary}</span>
      {total > 1 && (
        <div className="lk-pager-controls">
          <Item p={current - 1} className="lk-pager-step" disabled={current === 1} aria-label="Previous page">
            <i className="fas fa-chevron-left" aria-hidden="true" /> Previous
          </Item>
          <div className="lk-pager-pages" role="list">
            {pageWindow(current, total).map((p, i) => (
              p === ELLIPSIS ? (
                <span key={`gap-${i}`} className="lk-pager-gap" aria-hidden="true">{ELLIPSIS}</span>
              ) : (
                <Item
                  key={p}
                  p={p}
                  className={`lk-pager-page${p === current ? " is-current" : ""}`}
                  aria-current={p === current ? "page" : undefined}
                  aria-label={`Page ${p}`}
                >
                  {p}
                </Item>
              )
            ))}
          </div>
          <Item p={current + 1} className="lk-pager-step" disabled={current === total} aria-label="Next page">
            Next <i className="fas fa-chevron-right" aria-hidden="true" />
          </Item>
          {total > 7 && (
            <form className="lk-pager-jump" onSubmit={submitJump}>
              <label htmlFor="lk-pager-jump-input">Go to</label>
              <input
                id="lk-pager-jump-input"
                type="number"
                min={1}
                max={total}
                value={jump}
                onChange={(e) => setJump(e.target.value)}
                placeholder={String(current)}
                aria-label={`Go to page, 1 to ${total}`}
              />
              <button type="submit" className="lk-pager-step" disabled={!jump}>Go</button>
            </form>
          )}
        </div>
      )}
    </nav>
  );
};

export default TablePager;
